import { useHistory } from 'react-router-dom';
import { useExperiences } from 'core/contexts/experienceV2';
import { Box, Button, Grid2, IconButton, InputAdornment, useMediaQuery } from '@mui/material';
import { Chip, TextInput, ToggleButton } from '@utriper/core-ui';
import { Add, Clear, FormatListBulleted, GridView, Search, Tune } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ExperienceStatus, getTranslatedName, moods } from '@utriper/core';
import { useEffect, useState } from 'react';

const CHIP_TYPES = {
    STATUS: 1,
    MODUS: 2,
    LOCATION: 3,
};

const ExperienceFilters = () => {
    const { t, i18n } = useTranslation();
    const translate = (key) => t(`experience_list.filters.${key}`);
    const history = useHistory();
    const { handleToggleFilters, expName, handleSearch, expView, handleView, hasFilters, filters, setFilters } = useExperiences();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const [chips, setChips] = useState([]);
    const toggleButtonOptions = [
        {
            id: 'gallery',
            icon: GridView,
        },
        {
            id: 'table',
            icon: FormatListBulleted,
        },
    ];

    const handleAdd = () => history?.push('/experience/add');

    const handleRemoveFilter = ({ id, type }) => {
        setFilters(({ status, moduses, locationData, radius }) => ({
            status: type === CHIP_TYPES.STATUS ? status?.filter(({ id: statusId }) => statusId !== id) || [] : status,
            moduses: type === CHIP_TYPES.MODUS ? moduses?.filter(({ id: modusId }) => modusId !== id) || [] : moduses,
            locationData: type === CHIP_TYPES.LOCATION ? null : locationData,
            radius: type === CHIP_TYPES.LOCATION ? 10 : radius,
        }));
    };

    useEffect(() => {
        if (hasFilters()) {
            const { status = [], moduses = [], locationData = null, radius = 10 } = filters;

            let chipsToAdd = [];

            if (status?.length > 0) {
                chipsToAdd.push(...status?.map(({ id }) => ({ id, label: ExperienceStatus.getTranslatedName(ExperienceStatus.status, id, i18n.language), type: CHIP_TYPES.STATUS })));
            }

            if (moduses?.length > 0) {
                chipsToAdd.push(...moduses?.map(({ id }) => ({ id, label: getTranslatedName(moods, id, i18n.language), type: CHIP_TYPES.MODUS })));
            }

            if (typeof locationData === 'object' && locationData !== null) {
                const { locationId, location } = locationData;

                if (location?.length > 0 && locationId) {
                    chipsToAdd.push({ id: locationId, label: location, type: CHIP_TYPES.LOCATION });
                    chipsToAdd.push({ id: radius, label: `Radio: ${radius}`, type: CHIP_TYPES.LOCATION });
                }
            }

            setChips([...chipsToAdd]);
        }
    }, [filters, hasFilters, i18n.language]);

    return (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '16px' }}>
            <Grid2 container size={12} spacing={2} flexWrap={{ xs: 'wrap', md: 'nowrap' }} justifyContent="space-between">
                <Grid2 container size={{ xs: 9, md: 6 }} order={0}>
                    <TextInput
                        input={{
                            slotProps: {
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment:
                                        expName?.length > 0 ? (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => handleSearch('')}>
                                                    <Clear />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                },
                            },
                        }}
                        meta={{ error: null, touched: null }}
                        onChange={(event) => handleSearch(event?.target?.value)}
                        value={expName}
                        label={''}
                        placeholder={translate('name')}
                    />
                </Grid2>
                <Grid2 container size={{ xs: 12, md: 4 }} spacing={2} order={{ xs: 2, md: 1 }}>
                    <Button
                        sx={{
                            padding: '14px',
                            border: 'solid 1px #13131340',
                            borderRadius: '8px',
                            color: '#131313',
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                        onClick={handleToggleFilters}
                        variant="outlined"
                        startIcon={<Tune sx={{ color: '#131313', fontSize: '24px' }} />}
                    >
                        {translate('filter_button')}
                    </Button>
                    <ToggleButton
                        value={expView}
                        onChange={handleView}
                        options={toggleButtonOptions}
                        exclusive
                        sx={{
                            '& .MuiToggleButton-root': { borderRadius: '8px' },
                            '& .MuiToggleButtonGroup-firstButton': { borderTopRightRadius: '0 !important', borderBottomRightRadius: '0 !important' },
                            '& .MuiToggleButtonGroup-lastButton': { borderTopLeftRadius: '0 !important', borderBottomLeftRadius: '0 !important' },
                        }}
                    />
                </Grid2>
                <Grid2 container size={2} justifyContent="flex-end" order={{ xs: 1, md: 2 }}>
                    <Button
                        sx={{
                            backgroundColor: 'var(--color-utriper-blue)',
                            padding: '14px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: 'var(--color-utriper-blue-dark)',
                            },
                        }}
                        variant="contained"
                        startIcon={isMobile ? null : <Add sx={{ color: '#fff', fontSize: '24px' }} />}
                        onClick={handleAdd}
                    >
                        {isMobile ? <Add sx={{ color: '#fff', fontSize: '24px' }} /> : translate('add_button')}
                    </Button>
                </Grid2>
            </Grid2>

            {/* Filter Status */}
            {hasFilters() && chips?.length > 0 && (
                <Grid2 container size={12} spacing={2} alignItems="center">
                    {chips.map(({ id, label, type }) => (
                        <Chip
                            key={id}
                            label={label}
                            sx={{
                                background: '#137DC540',
                                color: 'var(--color-utriper-blue)',
                                padding: '4px',
                                boxSizing: 'initial',
                                borderRadius: '8px',
                                '& .MuiSvgIcon-root': {
                                    color: 'var(--color-utriper-blue)',
                                    '&:hover': {
                                        color: 'var(--color-utriper-blue-dark)',
                                    },
                                },
                            }}
                            onDelete={() => handleRemoveFilter({ id, type })}
                        />
                    ))}
                </Grid2>
            )}
        </Box>
    );
};

export default ExperienceFilters;
